import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Chip, Tooltip } from '@mui/material';
import '../../NavigationButton/NavigationButton.css';

export const actionChipHoverSx = {
  transition: 'border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease',
  '&:hover:not(.Mui-disabled)': {
    borderColor: '#F9AA2A',
    backgroundColor: 'rgba(249, 170, 42, 0.1)',
    '& .MuiChip-label': { color: '#F9AA2A' },
    '& .MuiChip-icon, & .MuiChip-icon svg, & .MuiChip-icon svg path': {
      color: '#F9AA2A !important',
      fill: 'currentColor',
    },
  },
};

/** Icon-only toolbar button with the same chrome as the filter controls around it. */
export const toolbarIconButtonSx = (accent = '#304FFF') => ({
  width: 'calc(var(--xtram-control-height, 36px) + 4px)',
  height: 'calc(var(--xtram-control-height, 36px) + 4px)',
  borderRadius: '8px',
  border: '2px solid #e5e7f6',
  backgroundColor: '#ffffff',
  color: accent,
  '&:hover': { borderColor: accent, backgroundColor: 'rgba(48, 79, 255, 0.06)' },
  '&.Mui-disabled': { color: '#bdbdbd', borderColor: '#eeeeee' },
});

export default function ActionChip({
  label,
  icon,
  borderColor = '#304FFF',
  onClick,
  disabled = false,
  direction,
  maxLabelWidth = 200,
  tooltip,
  className = '',
  sx = {},
}) {
  const labelRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const tooltipText = tooltip ?? label;

  const checkTruncation = useCallback(() => {
    const el = labelRef.current;
    if (el) {
      setTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [label, maxLabelWidth, checkTruncation]);

  const chip = (
    <Chip
      icon={icon}
      label={
        <span
          ref={labelRef}
          className="action-chip-label"
          style={{ maxWidth: maxLabelWidth }}
        >
          {label}
        </span>
      }
      onClick={onClick}
      clickable={!disabled}
      variant="outlined"
      disabled={disabled}
      dir={direction}
      className={className}
      sx={{
        borderColor,
        height: 'auto',
        py: '6px',
        maxWidth: '100%',
        '& .MuiChip-label': { fontSize: '12px', fontWeight: 600, display: 'flex' },
        '& .MuiChip-icon': { marginInlineStart: '8px' },
        ...actionChipHoverSx,
        ...sx,
      }}
    />
  );

  return (
    <Tooltip title={truncated ? tooltipText : ''} disableHoverListener={!truncated}>
      <span className="action-chip-tooltip-wrap">{chip}</span>
    </Tooltip>
  );
}
